import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { logUserAction } from "@/utils/analyticsUtils";
import { ImagePreviewItem } from "./image-upload/ImagePreviewItem";
import { ImageUploadButton } from "./image-upload/ImageUploadButton";
import { Loader2 } from "lucide-react";
import { ImageCropper } from "./image-upload/ImageCropper";
import { Progress } from "@/components/ui/progress";
import { verifyImage } from "@/utils/imageUtils";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { ImageGenerateButton } from "./image-upload/ImageGenerateButton";

interface ImagePreview {
  file: File;
  previewUrl: string;
  isExisting?: boolean;
  id?: string; // Add ID to track existing images
}

interface ServiceImagesUploadProps {
  images: ImagePreview[];
  setImages: React.Dispatch<React.SetStateAction<ImagePreview[]>>;
  coverImageIndex: number;
  setCoverImageIndex: (index: number) => void;
  serviceName?: string;
  uploadProgress?: number;
  isSubmitting?: boolean;
  deletedImageIds?: string[];
  setDeletedImageIds?: React.Dispatch<React.SetStateAction<string[]>>;
  serviceCategory?: string;
  description?: string;
}

export const ServiceImagesUpload = ({
  images,
  setImages,
  coverImageIndex,
  setCoverImageIndex,
  serviceName,
  uploadProgress = 0,
  isSubmitting = false,
  deletedImageIds = [],
  setDeletedImageIds = () => {},
  serviceCategory,
  description
}: ServiceImagesUploadProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const MAX_IMAGES = 10;
  const MAX_IMAGE_SIZE_MB = 5; // Maximum image size in MB

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    setIsUploading(true);

    const existingCount = images.length;
    const availableSlots = MAX_IMAGES - existingCount;
    
    if (files.length > availableSlots) {
      const plural = availableSlots === 1 ? '' : 's';
      toast({
        title: t('services.tooManyImages'),
        description: t('services.tooManyImagesDescription', { 
          count: availableSlots, 
          plural: plural, 
          max: MAX_IMAGES 
        }),
        variant: "default"
      });
    }

    // Process the first file for verification and cropping
    if (files.length > 0 && availableSlots > 0) {
      const file = files[0];
      
      try {
        // Verify the image meets requirements
        await verifyImage(file, MAX_IMAGE_SIZE_MB);
        
        // Store the file for later processing after cropping
        setTempFile(file);
        
        // Create preview for cropper
        const previewUrl = URL.createObjectURL(file);
        setTempImageUrl(previewUrl);
        setShowCropper(true);
      } catch (error) {
        console.error(`Error processing image ${file.name}:`, error);
        toast({
          title: t('services.invalidImage'),
          description: error instanceof Error ? error.message : "There was a problem processing the image. Please try again.",
          variant: "destructive",
        });
        setIsUploading(false);
      }
    } else if (availableSlots <= 0) {
      toast({
        title: t('services.maximumImagesReached'),
        description: t('services.maximumImagesDescription', { max: MAX_IMAGES }),
        variant: "default"
      });
      setIsUploading(false);
    }

    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleCropComplete = async (croppedImageUrl: string) => {
    if (!tempImageUrl || !tempFile) {
      setShowCropper(false);
      setTempImageUrl(null);
      setTempFile(null);
      setIsUploading(false);
      return;
    }

    try {
      // Convert base64 to blob
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      
      // Create a file with proper name and type
      const originalName = tempFile.name.replace(/\.[^/.]+$/, ""); // Remove extension
      const timestamp = Date.now();
      const file = new File([blob], `${originalName}-cropped-${timestamp}.jpg`, { 
        type: 'image/jpeg'
      });
      
      const newImages = [...images, { file, previewUrl: croppedImageUrl, isExisting: false }];
      setImages(newImages);
      
      // If this is the first image, make it the cover image
      if (images.length === 0) {
        setCoverImageIndex(0);
        toast({
          title: t('services.coverImageSet'),
          description: t('services.coverImageSetDescription'),
        });
      }
      
      // Log successful upload
      logUserAction('upload_service_images', 'ServiceImagesUpload', null, {
        count: 1,
        service: serviceName || 'new_service'
      });
    } catch (error) {
      console.error('Error processing cropped image:', error);
      toast({
        title: "Error processing image",
        description: "There was a problem processing the cropped image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowCropper(false);
      setTempImageUrl(null);
      setTempFile(null);
      setIsUploading(false);
    }
  };

  const handleImageGenerated = async (imageUrl: string) => {
    try {
      // Convert the blob URL to a File object
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create a file with proper name and type
      const timestamp = Date.now();
      const file = new File([blob], `ai-generated-${timestamp}.png`, { 
        type: 'image/png'
      });
      
      const newImages = [...images, { file, previewUrl: imageUrl, isExisting: false }];
      setImages(newImages);
      
      // If this is the first image, make it the cover image
      if (images.length === 0) {
        setCoverImageIndex(0);
        toast({
          title: t('services.coverImageSet'),
          description: t('services.coverImageSetDescription'),
        });
      }
      
      // Log successful AI image generation
      logUserAction('generate_service_image', 'ServiceImagesUpload', null, {
        service: serviceName || 'new_service'
      });
    } catch (error) {
      console.error('Error processing generated image:', error);
      toast({
        title: "Error processing image",
        description: "There was a problem processing the generated image. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Add this new function to immediately delete an image from the database and storage
  const deleteImageFromDatabase = async (imageId: string) => {
    if (!imageId) return false;
    
    try {
      // First get the image URL to be deleted so we can remove it from storage
      const { data: imageToRemove, error: fetchError } = await supabase
        .from('service_images')
        .select('id, image_url')
        .eq('id', imageId)
        .single();
        
      if (fetchError) {
        console.error("Error fetching image to remove:", fetchError);
        return false;
      }
      
      console.log(`Found image to remove from database:`, imageToRemove);
        
      // Delete the database record
      const { error: deleteError } = await supabase
        .from('service_images')
        .delete()
        .eq('id', imageId);
        
      if (deleteError) {
        console.error("Error deleting image record:", deleteError);
        return false;
      }
      
      console.log(`Successfully deleted image record from database`);
      
      // Now remove the file from storage
      if (imageToRemove && imageToRemove.image_url) {
        // Extract just the path part from the full URL
        const path = imageToRemove.image_url.replace(`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/service_images/`, '');
        
        if (path.includes('/')) {  // Ensure it's a valid path with a slash
          console.log(`Removing file from storage:`, path);
          
          const { error: storageError } = await supabase.storage
            .from('service_images')
            .remove([path]);
            
          if (storageError) {
            console.error("Error removing image file from storage:", storageError);
            return true; // Still return true since the database record was deleted
          }
          
          console.log("Successfully removed image file from storage");
        }
      }
      
      toast({
        title: t('services.imageDeleted'),
        description: t('services.imageDeletedDescription'),
      });
      
      return true;
    } catch (error) {
      console.error("Error deleting image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete image. Please try again.",
      });
      return false;
    }
  };

  const removeImage = async (index: number) => {
    // Add debugging to see what image is being removed
    console.log(`Removing image at index ${index}:`, images[index]);
    console.log(`Image has ID: ${Boolean(images[index]?.id)}, isExisting: ${Boolean(images[index]?.isExisting)}`);
    
    const imageToRemove = images[index];
    
    // If it's an existing image with an ID, delete it immediately from the database
    if (imageToRemove && imageToRemove.isExisting && imageToRemove.id) {
      console.log(`Deleting image with ID ${imageToRemove.id} from database immediately`);
      
      // Show loading state
      toast({
        title: t('services.deletingImage'),
        description: t('services.deletingImageDescription'),
      });
      
      const success = await deleteImageFromDatabase(imageToRemove.id);
      
      if (success) {
        // Still add to deletedImageIds to keep track (ensures consistency if database operation failed)
        setDeletedImageIds(prev => [...prev, imageToRemove.id!]);
        console.log(`Added image ID ${imageToRemove.id} to deleted list`);
      } else {
        // If immediate deletion failed, show error but still remove from UI
        toast({
          variant: "destructive",
          title: "Warning",
          description: "Image was removed from view but may not have been fully deleted from database.",
        });
      }
    }
    
    // Update the state (remove from UI)
    setImages((prev: ImagePreview[]) => {
      const newImages = [...prev];
      
      // Revoke object URL if it's a new image
      if (imageToRemove && !imageToRemove.isExisting) {
        URL.revokeObjectURL(imageToRemove.previewUrl);
      }
      
      newImages.splice(index, 1);
      return newImages;
    });
    
    if (images.length <= 1) {
      // If removing the last or only image, reset cover index to 0
      setCoverImageIndex(0);
    } else if (coverImageIndex === index) {
      // If removing the cover image, set first remaining image as cover
      setCoverImageIndex(0);
      
      toast({
        title: t('services.coverImageUpdated'),
        description: t('services.coverImageUpdatedDescription'),
      });
    } else if (coverImageIndex > index) {
      // Adjust cover index if removing image before the current cover
      setCoverImageIndex(coverImageIndex - 1);
    }
    
    // Log image removal
    logUserAction('remove_service_image', 'ServiceImagesUpload', null, {
      service: serviceName || 'new_service'
    });
  };

  const setCoverImage = (index: number) => {
    if (coverImageIndex === index) return;
    
    setCoverImageIndex(index);
    toast({
      title: t('services.coverImageUpdated'),
      description: t('services.coverImageUpdatedDescription'),
    });
    
    // Log cover image change
    logUserAction('set_cover_image', 'ServiceImagesUpload', null, {
      service: serviceName || 'new_service'
    });
  };

  return (
    <div>
      <Label>{t('services.imagesPortfolio')}</Label>
      <p className="text-sm text-gray-500 mb-2">
        {images.length > 0 
          ? t('services.setCoverImageInstruction')
          : t('services.addImagesInstruction')
        }
      </p>
      
      {isSubmitting && uploadProgress > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span>{t('services.uploadingImages')}</span>
            <span>{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}
      
      <div className="mt-1">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
          {images.map((image, index) => (
            <ImagePreviewItem
              key={`${image.previewUrl}-${index}`}
              imageUrl={image.previewUrl}
              index={index}
              isCoverImage={index === coverImageIndex}
              onRemove={removeImage}
              onSetCover={setCoverImage}
            />
          ))}
          
          {isUploading ? (
            <div className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <Loader2 className="w-8 h-8 text-gebeya-pink animate-spin" />
              <span className="mt-2 text-sm text-gray-500">{t('services.processingImage')}</span>
            </div>
          ) : (
            <>
              {images.length < MAX_IMAGES && (
                <ImageUploadButton 
                  onUpload={handleImageUpload} 
                  disabled={isSubmitting || isUploading}
                />
              )}
              {images.length < MAX_IMAGES && (
                <ImageGenerateButton
                  onImageGenerated={handleImageGenerated}
                  disabled={isSubmitting || isUploading}
                  serviceName={serviceName}
                  serviceCategory={serviceCategory}
                  description={description}
                />
              )}
            </>
          )}
        </div>
      </div>
      
      {showCropper && tempImageUrl && (
        <ImageCropper
          imageUrl={tempImageUrl}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCropper(false);
            setTempImageUrl(null);
            setTempFile(null);
            setIsUploading(false);
          }}
          aspectRatio={1}
        />
      )}
    </div>
  );
};
